import React, { useState } from "react";
import { flushSync } from "react-dom";
import { Button, CircularProgress, Box } from "@mui/material";
import { useTranslation } from "react-i18next";
import {
  ContentContainer,
  ModalTitle,
  ModalText,
  ButtonsContainer,
} from "../ApproveConfirmation/ApproveConfirmationModal.styled";
import { CrossButton } from "../Modal.styled";
import { CrossIcon } from "../../Icons";
import { colors } from "../../../theme/colors";

interface ReApproveConfirmationModalProps {
  onClose: () => void;
  onReApprove: () => Promise<void>;
  onLoadingChange?: (loading: boolean) => void;
}

const ReApproveConfirmationModalContent: React.FC<
  ReApproveConfirmationModalProps
> = ({ onClose, onReApprove, onLoadingChange }) => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);

  const handleReApprove = async () => {
    // flushSync ensures the loading state renders immediately before the async
    // work starts — without it, React 18 auto-batching can swallow the
    // true→false transition when the operation resolves in a microtask.
    flushSync(() => {
      setIsLoading(true);
    });
    onLoadingChange?.(true);
    try {
      await onReApprove();
    } finally {
      setIsLoading(false);
      onLoadingChange?.(false);
    }
  };

  return (
    <ContentContainer>
      {/* Custom X button inside content so it is aware of isLoading */}
      <CrossButton onClick={onClose} disabled={isLoading} size="small">
        <CrossIcon width="32px" height="32px" fill={colors.text.primary} />
      </CrossButton>

      <ModalTitle>{t("reApproveConfirmation.title")}</ModalTitle>
      <ModalText>{t("reApproveConfirmation.message")}</ModalText>

      <ButtonsContainer>
        <Button variant="outlined" onClick={onClose} disabled={isLoading}>
          {t("common.cancel")}
        </Button>
        <Button
          variant="contained"
          onClick={handleReApprove}
          disabled={isLoading}
          sx={{
            backgroundColor: colors.amber.main,
            "&:hover": {
              backgroundColor: colors.amber.dark,
            },
            minWidth: "160px",
          }}
        >
          {isLoading ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <CircularProgress size={16} sx={{ color: "#fff", flexShrink: 0 }} />
              {t("reApproveConfirmation.updatingPlan")}
            </Box>
          ) : (
            t("reApproveConfirmation.reApproveButton")
          )}
        </Button>
      </ButtonsContainer>
    </ContentContainer>
  );
};

export default ReApproveConfirmationModalContent;
